/**
 * brandImport: the BRAND dialect of the shared catalogue importer.
 * ───────────────────────────────────────────────────────────────────────────
 * The parsing, the validation and every rule that is not brand-specific live
 * in catalogueImport.js. What is left here is what only a brand has: a lead
 * time, a bulk price, a three-tier trade price list, an order-flow override,
 * and per-brand product attributes carried in `spec:` columns.
 *
 * Every symbol this file used to export it still exports, so BrandImportModal,
 * the tests and anything else that imports from here did not have to change.
 *
 * The sibling dialects are healthcareImport.js (pharmacies) and, for
 * restaurants, the older utilities/menuImport.js, which predates the shared
 * layer and has not been folded into it.
 *
 * THREE BRAND RULES THE RESTAURANT IMPORTER DOES NOT HAVE, now shared with
 * healthcare because none of them was ever brand-specific:
 *
 *   1. NO OPTION GROUPS. Brands have none, so the restaurant runner's middle
 *      phase disappears and the workbook loses a whole sheet.
 *   2. AN AVAILABILITY GATE. Rows that are not in stock are SKIPPED with a
 *      stated reason, not imported and not silently dropped. `countInStock: 0`
 *      is unreachable through the API anyway, so an out-of-stock row would
 *      import as "1 in stock", which is a lie a catalogue cannot afford.
 *   3. DUPLICATE DETECTION. The restaurant importer is not idempotent and
 *      re-running it duplicates every row. Here a row whose sku_code (or
 *      section + name) already exists on the brand is marked "already
 *      imported" and skipped, so a partial import is resumed by re-running the
 *      same workbook.
 *
 * BRAND-AGNOSTIC BY CONSTRUCTION. Nothing here names a brand. Per-brand
 * attributes ride in `spec:<fieldKey>` columns validated against that brand's
 * own productSpecTemplate, so Hisense's model/size/warranty and a furniture
 * brand's material/finish use the same template file and the same parser.
 */
import { canonicalHeader, labelOf } from "./brandImportColumns.js";
import { createCatalogueImport, norm, parseMoney } from "./catalogueImport.js";

export {
  MAX_ADDITIONAL_IMAGES,
  PAYMENT_METHODS,
  parseBool,
  parseList,
  parseMoney,
  parseStatus,
  readAvailability,
  readSpecColumns,
} from "./catalogueImport.js";

/* brandProductSchema.orderFlowOverride. INHERIT = follow the brand's flow. */
export const ORDER_FLOWS = Object.freeze([
  "INHERIT",
  "STANDARD",
  "BRANDED_PACKAGING",
  "QUOTE",
]);

/** The three trade tiers a brand may quote, and their column keys. */
const TIER_COLUMNS = Object.freeze({
  retailer: "tier_price_retailer",
  wholesaler: "tier_price_wholesaler",
  healthcare: "tier_price_healthcare",
});

const readTierPrices = (r) => {
  const tiers = {};
  for (const [tier, key] of Object.entries(TIER_COLUMNS)) {
    const n = parseMoney(r[key]);
    if (n !== null) tiers[tier] = n;
  }
  return tiers;
};

const dialect = {
  canonicalHeader,
  labelOf,
  vendorNoun: "brand",
  supportsSpecs: true,

  readExtras: (r, { parseMoney: money, enumValue }) => ({
    leadTimeDays: money(r.lead_time_days),
    bulkPrice: money(r.bulk_price_ugx),
    tierPrices: readTierPrices(r),
    orderFlowOverride: enumValue(r.order_flow_override),
  }),

  validateExtras: ({ item, ctx, errors, warnings, P }) => {
    const flow = item.orderFlowOverride || "INHERIT";
    if (!ORDER_FLOWS.includes(flow)) {
      errors.push(
        `${P("order_flow_override")} "${item.orderFlowOverride}" is not one of ${ORDER_FLOWS.join(" / ")}`
      );
    }

    if (item.leadTimeDays !== null && item.leadTimeDays < 0) {
      errors.push(`${P("lead_time_days")} cannot be negative`);
    }
    if (item.bulkPrice !== null && item.bulkPrice < 0) {
      errors.push(`${P("bulk_price_ugx")} cannot be negative`);
    }

    /* Specs against THIS brand's template. The server does not validate specs
       at all ("we trust the dashboard form to align field keys"), so this is
       the only place a typo'd field key can be caught before it reaches a
       product page as a spec nobody's storefront knows how to render. */
    const specTemplate = ctx.specTemplate || null;
    const templateKeys = new Set(
      (specTemplate?.fields || []).map((f) => norm(f.fieldKey))
    );
    const templateTypeByKey = new Map(
      (specTemplate?.fields || []).map((f) => [norm(f.fieldKey), f.fieldType])
    );

    const specs = {};
    for (const [key, value] of Object.entries(item.specs)) {
      if (templateKeys.size && !templateKeys.has(norm(key))) {
        warnings.push(
          `spec "${key}" is not in this brand's spec template and may not render on the product page`
        );
      }
      const declaredType = templateTypeByKey.get(norm(key));
      if (declaredType === "number") {
        const n = parseMoney(value);
        if (n === null) {
          warnings.push(
            `spec "${key}" is declared as a number but "${value}" is not one`
          );
          specs[key] = value;
        } else {
          specs[key] = n;
        }
      } else {
        specs[key] = value;
      }
    }

    return { specs, orderFlowOverride: flow };
  },
};

const importer = createCatalogueImport(dialect);

export const findHeaderRow = importer.findHeaderRow;
export const pickSheet = importer.pickSheet;
export const parseWorkbook = importer.parseWorkbook;

/**
 * @param {object} parsed        output of parseWorkbook
 * @param {File[]} imageFiles    photos the operator picked
 * @param {object} ctx
 * @param {Array}  ctx.existingSections   GET /api/brands/:id/sections
 * @param {Array}  ctx.platformCategories [{_id, category}] from /category/get-all
 * @param {Array}  ctx.units              [{_id, unit}] from /unit
 * @param {object} ctx.specTemplate       brand.productSpecTemplate
 */
export const validateImport = importer.validateImport;

export const buildImportPlan = importer.buildImportPlan;

export default buildImportPlan;
